import { useEffect, useState } from "react"

import { differenceInDays, format } from "date-fns"

import { formatUTCStringDate } from "src/components/Account/BillingPortal/ChangePlan/utils"
import {
  CANCEL_PLAN_CONTACT_FORM_ID,
  CancelPlanContactStep,
} from "src/components/Account/BillingPortal/YourPlan/CancelPlanDialogWizard/CancelPlanContactStep"
import {
  CANCEL_PLAN_REASON_FORM_ID,
  CancelPlanReasonStep,
} from "src/components/Account/BillingPortal/YourPlan/CancelPlanDialogWizard/CancelPlanReasonStep"
import { useCancelPlanForm } from "src/components/Account/BillingPortal/YourPlan/CancelPlanDialogWizard/useCancelPlanForm"
import { CustomPlanInfoBox } from "src/components/Account/BillingPortal/YourPlan/CustomPlanInfoBox"
import {
  usePostTrackCancelCompleteEvent,
  usePostTrackCancelInitEvent,
} from "src/data/analytics/queries/planAnalyticsQueries"
import {
  useFetchAvailablePlansForUser,
  useFetchCurrentSubscription,
  usePostCancelSubscription,
} from "src/data/billing/queries/billingQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { ConfirmDialog } from "src/ui/Dialog/ConfirmDialog"
import { DialogWizard } from "src/ui/Wizard/DialogWizard"
import { ErrorService } from "src/utils/ErrorService"

export function CancelPlanDialogWizard({ hasCSM }: { hasCSM: boolean }) {
  const { _t, langKeys } = useTranslate()
  const { navigate, goBack } = useRouter()
  const user = useGetUser()

  const [currentFormStepIndex, setCurrentFormStepIndex] = useState(0)

  const form = useCancelPlanForm({
    onSubmit: () => {
      cancelPlan()
    },
  })

  const fetchCurrentSubscription = useFetchCurrentSubscription({
    userId: user.user_id,
  })
  const postCancelSubscription = usePostCancelSubscription()
  const postTrackCancelCompleteEvent = usePostTrackCancelCompleteEvent()
  const postTrackCancelInitEvent = usePostTrackCancelInitEvent()
  const fetchAvailablePlansForUser = useFetchAvailablePlansForUser({
    userId: user.user_id,
  })
  const subscription = fetchCurrentSubscription.data
  const currentPlan = fetchAvailablePlansForUser.data?.current_plan

  const today = new Date()

  // Send a plan cancel initiated event for tracking
  // next_billing_at is an optional prop but is required to calculate the remaining days.
  // The cancel plan initialised event is also required to have this remaining_days prop sent.
  useEffect(() => {
    if (subscription?.next_billing_at) {
      const remainingDays = differenceInDays(
        new Date(subscription.next_billing_at),
        today
      )

      postTrackCancelInitEvent.mutate({ remaining_days: remainingDays })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscription?.next_billing_at])

  async function cancelPlan() {
    if (!subscription?.next_billing_at) {
      throw Error(
        "Undefined subscription or next billing date when trying to cancel plan"
      )
    }

    if (
      !form.values.cancelReason ||
      !form.values.experienceRating ||
      !form.values.returnProbability
    ) {
      // The submit button is already disabled  based on cancelReason existance
      // so technically this shouldn't happen
      throw Error(
        "Required fields are not set, but ideally this should not happen"
      )
    }

    const remainingDays = differenceInDays(
      new Date(subscription.next_billing_at),
      today
    )

    postTrackCancelCompleteEvent.mutate({
      cancellation_date: format(new Date(), "yyyy-MM-dd"),
      cancel_reason: form.values.cancelReason,
      cancel_comment: form.values.additionalFeedback,
      experience_rating: form.values.experienceRating,
      return_probability: form.values.returnProbability,
      remaining_days: remainingDays,
      phone_number: form.values.phoneNumber,
    })

    try {
      await postCancelSubscription.mutateAsync({
        subscriptionId: subscription.subscription_id,
        subscriptionBody: {
          end_of_term: true,
        },
      })
      navigate(Routes.ChangePlanSuccess.location())
    } catch (error) {
      ErrorService.captureException(error)
    }
  }

  function handleOnClose() {
    goBack({ defaultPath: Routes.AccountBilling.location() })
  }

  if (
    currentPlan?.custom_plan ||
    hasCSM ||
    subscription?.billing_model !== "plan_unit_per_home"
  ) {
    return (
      <ConfirmDialog
        title={_t(langKeys.cancel_plan_title)}
        onClose={handleOnClose}
        onConfirm={() => handleOnClose()}
        confirmLabel={_t(langKeys.close)}
        open={true}
      >
        <CustomPlanInfoBox />
      </ConfirmDialog>
    )
  }

  return (
    <DialogWizard
      steps={[
        {
          title: _t(langKeys.cancel_plan_title),
          description: _t(langKeys.cancel_plan_body, {
            date: formatUTCStringDate(subscription.next_billing_at),
          }),
          component: (
            <CancelPlanReasonStep
              form={form}
              onSubmit={() => setCurrentFormStepIndex(1)}
            />
          ),
          onBack: handleOnClose,
          backButtonLabel: _t(langKeys.back),
          nextButtonLabel: _t(langKeys.continue),
          formId: CANCEL_PLAN_REASON_FORM_ID,
        },
        {
          title: _t(langKeys.cancel_plan_title),
          description: _t(langKeys.cancel_plan_body, {
            date: formatUTCStringDate(subscription.next_billing_at),
          }),
          component: (
            <CancelPlanContactStep form={form} onSubmit={form.handleSubmit} />
          ),
          backButtonLabel: _t(langKeys.back),
          nextButtonLabel: _t(langKeys.cancel_plan),
          confirmButtonProps: {
            color: "destructive",
          },
          formId: CANCEL_PLAN_CONTACT_FORM_ID,
        },
      ]}
      currentStep={currentFormStepIndex}
      onBack={() => setCurrentFormStepIndex(currentFormStepIndex - 1)}
      onNext={() => setCurrentFormStepIndex(currentFormStepIndex + 1)}
      open={true}
      loading={
        postCancelSubscription.isLoading || fetchAvailablePlansForUser.isLoading
      }
      error={
        postCancelSubscription.isError &&
        _t(langKeys.failed_something_went_wrong)
      }
    />
  )
}
